import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Chip } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';

// スタイル付きコンポーネントの定義
const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: linear-gradient(145deg, #ffffff, #f0f0f0);
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const MotionDialogContent = styled(motion.div)`
  padding: 20px;
`;

const ContentSection = styled(motion.div)`
  margin-bottom: 24px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const TournamentDetailModal = ({ open, onClose, tournament }) => {
  // アニメーションバリアント
  const modalVariants = {
    hidden: {
      scale: 0.5,
      opacity: 0,
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.3,
      },
    },
    exit: {
      scale: 0.5,
      opacity: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
  };

  return (
    <AnimatePresence mode="wait">
      {open && (
        <StyledDialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          PaperComponent={motion.div}
          PaperProps={{
            initial: "hidden",
            animate: "visible",
            exit: "exit",
            variants: modalVariants,
          }}
        >
          <DialogTitle
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            sx={{
              background: 'linear-gradient(45deg, #3b82f6 30%, #6366f1 90%)',
              color: 'white',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
            }}
          >
            {tournament?.tournamentName || tournament?.title || '大会詳細'}
          </DialogTitle>

          <DialogContent>
            <MotionDialogContent>
              <ContentSection
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                custom={0}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#1e40af' }}>
                  開催情報
                </Typography>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'auto 1fr' }}>
                  <Typography variant="subtitle2">開催日：</Typography>
                  <Typography>{tournament?.date ? new Date(tournament.date).toLocaleDateString('ja-JP') : '未定'}</Typography>
                  <Typography variant="subtitle2">時間：</Typography>
                  <Typography>{tournament?.time || '未定'}</Typography>
                  <Typography variant="subtitle2">会場：</Typography>
                  <Typography>{tournament?.venue || '未定'}</Typography>
                  {tournament?.venueLocation && (
                    <>
                      <Typography variant="subtitle2">会場住所：</Typography>
                      <Typography>{tournament.venueLocation}</Typography>
                    </>
                  )}
                </Box>
              </ContentSection>

              <ContentSection
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                custom={1}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#1e40af' }}>
                  カテゴリー
                </Typography>
                <Typography>{tournament?.category || '未定'}</Typography>
              </ContentSection>

              {tournament?.matchups && tournament.matchups.length > 0 && (
                <ContentSection
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  custom={2}
                >
                  <Typography variant="h6" gutterBottom sx={{ color: '#1e40af' }}>
                    対戦カード
                  </Typography>
                  {tournament.matchups.map((matchup, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {matchup.round || `対戦 ${index + 1}`}
                        </Typography>
                        {matchup.boFormat && (
                          <Chip 
                            label={matchup.boFormat} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {matchup.teams[0]?.name || 'チーム1'}
                        </Typography>
                        <Typography variant="h6" color="primary">VS</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {matchup.teams[1]?.name || 'チーム2'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </ContentSection>
              )}

              {tournament?.broadcast && tournament.broadcast.length > 0 && (
                <ContentSection
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  custom={3}
                >
                  <Typography variant="h6" gutterBottom sx={{ color: '#1e40af' }}>
                    配信情報
                  </Typography>
                  {tournament.broadcast.map((stream, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {stream.platform}: {stream.url || '未定'}
                      </Typography>
                    </Box>
                  ))}
                </ContentSection>
              )}

              {tournament?.officialSite && (
                <ContentSection
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  custom={4}
                >
                  <Typography variant="h6" gutterBottom sx={{ color: '#1e40af' }}>
                    公式サイト
                  </Typography>
                  <Typography 
                    component="a" 
                    href={tournament.officialSite} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {tournament.officialSite}
                  </Typography>
                </ContentSection>
              )}
            </MotionDialogContent>
          </DialogContent>

          <DialogActions
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            sx={{ padding: 2 }}
          >
            <Button
              onClick={onClose}
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #3b82f6 30%, #6366f1 90%)',
                boxShadow: '0 3px 5px 2px rgba(59, 130, 246, .3)',
              }}
            >
              閉じる
            </Button>
          </DialogActions>
        </StyledDialog>
      )}
    </AnimatePresence>
  );
};

export default TournamentDetailModal;
